import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * CoverageTrendChart — daily Coverage % line for the Ops Dashboard.
 *
 * Backend contract (`GET /api/metrics/operational/timeseries`):
 *   { series: [{ date: "YYYY-MM-DD", scheduled_hours, worked_hours, coverage_pct }], ... }
 *
 * Displays:
 *   - Line of coverage_pct over the window
 *   - 100% reference line (perfect fulfillment)
 *   - 80% reference line (typical target)
 *   - Trend arrow + delta vs. first-day baseline
 *   - Tooltip with scheduled + worked hours per day
 */
export default function CoverageTrendChart({ start, end, siteId, cadenceLabel, refreshTick = 0 }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { start, end };
        if (siteId) params.site_id = siteId;
        const r = await api.get("/metrics/operational/timeseries", { params });
        if (!cancelled) setSeries(r.data?.series || []);
      } catch {
        if (!cancelled) setSeries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [start, end, siteId, refreshTick]);

  // Truncate date labels to short "MM/DD" for readability on the axis.
  const chartData = useMemo(
    () => series.map((s) => ({
      ...s,
      label: (() => {
        const d = new Date(s.date + "T00:00:00Z");
        return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
      })(),
    })),
    [series]
  );

  // Trend calc: compare average of first third vs. last third to smooth
  // out single-day spikes. Neutral if < 3 data points.
  const trend = useMemo(() => {
    const withData = chartData.filter((r) => r.scheduled_hours > 0);
    if (withData.length < 3) return { arrow: "flat", delta: 0, avg: null };
    const third = Math.max(1, Math.floor(withData.length / 3));
    const first = withData.slice(0, third);
    const last  = withData.slice(-third);
    const avg = (arr) => arr.reduce((s, r) => s + r.coverage_pct, 0) / arr.length;
    const firstAvg = avg(first);
    const lastAvg = avg(last);
    const delta = +(lastAvg - firstAvg).toFixed(1);
    const totalAvg = +(avg(withData)).toFixed(1);
    return {
      arrow: Math.abs(delta) < 0.5 ? "flat" : (delta > 0 ? "up" : "down"),
      delta,
      avg: totalAvg,
    };
  }, [chartData]);

  const TrendIcon = trend.arrow === "up" ? TrendingUp : trend.arrow === "down" ? TrendingDown : Minus;
  const trendColor = trend.arrow === "up" ? "#22C55E" : trend.arrow === "down" ? "#FF3B30" : "#a1a1aa";

  return (
    <div className="border border-[#27272A] bg-[#0A0A0A] p-5" data-testid="coverage-trend-chart">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <h3 className="font-display uppercase font-bold text-lg tracking-tight text-zinc-100">
            Coverage Trend
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            Worked ÷ Scheduled hours per day{siteId ? " · site-scoped" : ""} · <span className="text-zinc-300">{cadenceLabel}</span>
          </p>
        </div>
        {trend.avg !== null && (
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Window avg</div>
              <div className="font-mono text-2xl text-zinc-100" data-testid="coverage-window-avg">
                {trend.avg}%
              </div>
            </div>
            <div className="text-right border-l border-[#27272A] pl-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Trend</div>
              <div className="flex items-center gap-1.5 font-mono text-2xl" style={{ color: trendColor }} data-testid="coverage-trend-delta">
                <TrendIcon className="size-5" />
                {trend.arrow === "flat" ? "flat" : `${trend.delta > 0 ? "+" : ""}${trend.delta}pt`}
              </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center text-xs text-zinc-500">Loading trend…</div>
      ) : chartData.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-xs text-zinc-500">
          No shifts in this window.
        </div>
      ) : (
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "#a1a1aa", fontSize: 10 }}
                stroke="#3F3F46"
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: "#a1a1aa", fontSize: 10 }}
                stroke="#3F3F46"
                domain={[0, (dataMax) => Math.max(100, Math.ceil((dataMax || 100) / 10) * 10)]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  background: "#0A0A0A",
                  border: "1px solid #27272A",
                  fontSize: 11,
                }}
                labelStyle={{ color: "#a1a1aa", fontWeight: 700, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.1em" }}
                itemStyle={{ color: "#e4e4e7" }}
                formatter={(value, name) => {
                  if (name === "coverage_pct") return [`${value}%`, "Coverage"];
                  if (name === "scheduled_hours") return [`${value} hrs`, "Scheduled"];
                  if (name === "worked_hours") return [`${value} hrs`, "Worked"];
                  return [value, name];
                }}
              />
              <ReferenceLine y={100} stroke="#22C55E" strokeDasharray="4 4" strokeOpacity={0.4}
                label={{ value: "100%", fill: "#22C55E", fontSize: 9, position: "insideRight" }} />
              <ReferenceLine y={80} stroke="#FF9F0A" strokeDasharray="4 4" strokeOpacity={0.4}
                label={{ value: "target", fill: "#FF9F0A", fontSize: 9, position: "insideRight" }} />
              <Line
                type="monotone"
                dataKey="coverage_pct"
                stroke="#0A84FF"
                strokeWidth={2}
                dot={{ r: 3, fill: "#0A84FF", stroke: "#0A84FF" }}
                activeDot={{ r: 5, fill: "#FF3B30" }}
                isAnimationActive
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
